import React from "react";
import { Box, CircularProgress, Typography, Backdrop, useTheme } from "@mui/material";

/**
 * Componente LoadingSpinner reutilizable
 * Proporciona diferentes variantes de indicadores de carga
 */
const LoadingSpinner = ({
  // Variantes del spinner
  variant = "overlay", // 'overlay', 'inline', 'centered', 'backdrop'

  // Tamaño del spinner
  size = 40,

  // Mensaje de carga
  message = "Cargando...",

  // Mostrar/ocultar mensaje
  showMessage = true,

  // Color del spinner
  color = "primary",

  // Estilos personalizados
  sx = {},

  // Props específicas para backdrop
  backdropOpen = true,

  // Altura mínima para variante centered
  minHeight = "200px",

  // Espesor del spinner
  thickness = 3.6,

  // Props adicionales
  ...props
}) => {
  const theme = useTheme();

  // Componente base del spinner
  const SpinnerComponent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        ...sx,
      }}
      {...props}>
      <CircularProgress
        size={size}
        color={color}
        thickness={thickness}
        sx={{
          animation: "spin 1s linear infinite",
          "@keyframes spin": {
            "0%": {
              transform: "rotate(0deg)",
            },
            "100%": {
              transform: "rotate(360deg)",
            },
          },
        }}
      />
      {showMessage && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 1,
            textAlign: "center",
            fontSize: "0.875rem",
          }}>
          {message}
        </Typography>
      )}
    </Box>
  );

  // Spinner con overlay absoluto
  if (variant === "overlay") {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(2px)",
          zIndex: theme.zIndex.modal - 1,
          ...sx,
        }}
        {...props}>
        <SpinnerComponent />
      </Box>
    );
  }

  // Spinner con backdrop completo
  if (variant === "backdrop") {
    return (
      <Backdrop
        open={backdropOpen}
        sx={{
          zIndex: theme.zIndex.modal + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          ...sx,
        }}
        {...props}>
        <SpinnerComponent />
      </Backdrop>
    );
  }

  // Spinner centrado con altura mínima
  if (variant === "centered") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: minHeight,
          width: "100%",
          ...sx,
        }}
        {...props}>
        <SpinnerComponent />
      </Box>
    );
  }

  // Spinner inline (por defecto)
  return <SpinnerComponent />;
};

/**
 * Componente de skeleton loading para contenido específico
 */
export const SkeletonLoader = ({
  variant = "rectangular", // 'text', 'rectangular', 'circular'
  width = "100%",
  height = 40,
  animation = "pulse", // 'pulse', 'wave', false
  count = 1,
  sx = {},
  ...props
}) => {
  const skeletons = Array.from({ length: count }, (_, index) => (
    <Box
      key={index}
      sx={{
        width,
        height: variant === "text" ? "1em" : height,
        backgroundColor: "rgba(0, 0, 0, 0.11)",
        borderRadius: variant === "circular" ? "50%" : 1,
        animation:
          animation === "pulse"
            ? "skeleton-pulse 1.5s ease-in-out infinite"
            : animation === "wave"
            ? "skeleton-wave 1.6s linear infinite"
            : "none",
        "@keyframes skeleton-pulse": {
          "0%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.4,
          },
          "100%": {
            opacity: 1,
          },
        },
        "@keyframes skeleton-wave": {
          "0%": {
            transform: "translateX(-100%)",
          },
          "50%": {
            transform: "translateX(100%)",
          },
          "100%": {
            transform: "translateX(700%)",
          },
        },
        mb: count > 1 && index < count - 1 ? 1 : 0,
        ...sx,
      }}
      {...props}
    />
  ));

  return count === 1 ? skeletons[0] : <Box>{skeletons}</Box>;
};

/**
 * Componente de loading para listas
 */
export const ListLoadingSkeleton = ({ items = 5, showAvatar = false, showSecondary = true, sx = {}, ...props }) => {
  return (
    <Box sx={{ ...sx }} {...props}>
      {Array.from({ length: items }, (_, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            gap: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}>
          {showAvatar && <SkeletonLoader variant="circular" width={40} height={40} />}
          <Box sx={{ flexGrow: 1 }}>
            <SkeletonLoader variant="text" width="60%" height={20} sx={{ mb: showSecondary ? 1 : 0 }} />
            {showSecondary && <SkeletonLoader variant="text" width="40%" height={16} />}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Componente de loading para cards
 */
export const CardLoadingSkeleton = ({ cards = 3, showImage = true, showActions = true, sx = {}, ...props }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: 2,
        ...sx,
      }}
      {...props}>
      {Array.from({ length: cards }, (_, index) => (
        <Box
          key={index}
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            overflow: "hidden",
          }}>
          {showImage && <SkeletonLoader variant="rectangular" width="100%" height={200} />}
          <Box sx={{ p: 2 }}>
            <SkeletonLoader variant="text" width="80%" height={24} sx={{ mb: 1 }} />
            <SkeletonLoader variant="text" width="100%" height={16} sx={{ mb: 1 }} />
            <SkeletonLoader variant="text" width="60%" height={16} sx={{ mb: showActions ? 2 : 0 }} />
            {showActions && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <SkeletonLoader variant="rectangular" width={80} height={36} />
                <SkeletonLoader variant="rectangular" width={80} height={36} />
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Componente de loading para tablas
 */
export const TableLoadingSkeleton = ({ rows = 5, columns = 4, showHeader = true, sx = {}, ...props }) => {
  return (
    <Box sx={{ ...sx }} {...props}>
      {showHeader && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 2,
            p: 2,
            borderBottom: "2px solid rgba(0, 0, 0, 0.12)",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          }}>
          {Array.from({ length: columns }, (_, index) => (
            <SkeletonLoader key={index} variant="text" width="80%" height={20} />
          ))}
        </Box>
      )}
      {Array.from({ length: rows }, (_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 2,
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}>
          {Array.from({ length: columns }, (_, colIndex) => (
            <SkeletonLoader key={colIndex} variant="text" width="90%" height={16} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default LoadingSpinner;
